<script setup lang="ts">
import { ref } from 'vue'

import type { HTMLAttributeClass } from '@fondof-ui/types'
import { localizeUrl } from '@internal/locale'
import FilterList from '@storefront-satch/components/molecules/filter/FilterList.vue'
import type { Filter } from '@storefront-satch/components/molecules/filter/types'
import { t } from '@storefront-satch/lib/translations'

import { ChevronIcon, FilterIcon } from '@fond-of/satch-icons'

type Props = {
  filter: Filter[]
  selectedFilter: Record<string, string[]>
  query?: string
  resetPage?: boolean
  pathname: string
  disallowedSortOptions?: string[]
} & HTMLAttributeClass

const props = withDefaults(defineProps<Props>(), {
  query: undefined,
  resetPage: true,
  class: '',
  disallowedSortOptions: () => [],
})

const selectedFilter = Object.keys(props.selectedFilter).length

const showFilter = ref(selectedFilter > 0)
</script>

<template>
  <div
    :class="[
      'flex h-full w-full flex-col items-center justify-start',
      props.class,
    ]"
  >
    <div
      class="group/filter-navigation inline-flex w-full cursor-pointer items-center justify-start gap-2"
      :data-state="showFilter ? 'open' : 'closed'"
    >
      <div
        class="inline-flex h-full items-center justify-center gap-2"
        @click="showFilter = !showFilter"
      >
        <FilterIcon class="size-5 text-black" />

        <div class="inline-flex h-full items-center justify-center">
          <span class="font-sans-semi-bold text-base leading-none md:text-xs">
            {{ t('filter.title') }}
          </span>

          <span
            v-if="selectedFilter"
            class="ml-1"
          >
            ({{ selectedFilter }})
          </span>

          <ChevronIcon
            :class="[
              'ml-2 size-3.5 transition ease-in-out',
              'rotate-0 group-data-[state=open]/filter-navigation:rotate-180',
            ]"
          />
        </div>
      </div>

      <a
        :href="
          localizeUrl(
            props.pathname,
            props.query
              ? {
                  q: props.query,
                }
              : undefined
          )
        "
        class="ml-2 font-sans text-xs"
        :class="{
          'text-gray-light': selectedFilter === 0,
          'text-primary': selectedFilter > 0,
        }"
      >
        {{ t('filter.resetAll') }}
      </a>
    </div>

    <div class="w-full">
      <transition
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="max-h-0 opacity-0"
        enter-to-class="max-h-[500px] opacity-100"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="max-h-[500px] opacity-100"
        leave-to-class="max-h-0 opacity-0"
      >
        <FilterList
          v-if="showFilter"
          :filter="props.filter"
          :selected-filter="props.selectedFilter"
          :disallowed-sort-options="props.disallowedSortOptions"
        />
      </transition>
    </div>
  </div>
</template>
